import buildQuery from 'odata-query'
import { createAsyncThunk } from '@reduxjs/toolkit'
import {
  ClosingAgreementRequirementsModel,
  ClosingAgreementRequirementsRequestModel,
  ClosingPaymentRequirementsModel,
  ScheduleSettingsModel,
} from './typings'
import { ServiceConfig } from '../services/config'
import { APIReject, get, put } from '../services/crud'
import { ENTITY_TYPES, type ODataResponse } from '@sales-center/common'
import type { ILead } from '../lead'
import type { IOpportunity } from '../opportunity'

const baseUrl = `${ServiceConfig.baseAPIUrl}/ClosingRequirements`

export const getAgreementRequirementSettings = createAsyncThunk<
  ClosingAgreementRequirementsModel[],
  void,
  { rejectValue: APIReject }
>('closingRequirements/getAgreementRequirements', async (_, thunkApi) => {
  const response = await get(`${baseUrl}/agreement`)
  if (response.ok) return await response.json()
  return thunkApi.rejectWithValue((await response.json()) as APIReject)
})

export const getPaymentRequirementSettings = createAsyncThunk<
  ClosingPaymentRequirementsModel[],
  void,
  { rejectValue: APIReject }
>('closingRequirements/getPaymentRequirements', async (_, thunkApi) => {
  const response = await get(`${baseUrl}/payment`)
  if (response.ok) return await response.json()
  return thunkApi.rejectWithValue((await response.json()) as APIReject)
})

export const getSchedulingRequirementSettings = createAsyncThunk<
  ScheduleSettingsModel,
  void,
  { rejectValue: APIReject }
>('closingRequirements/getScheduleRequirements', async (_, thunkApi) => {
  const response = await get(`${baseUrl}/scheduling`)
  if (response.ok) return await response.json()
  return thunkApi.rejectWithValue((await response.json()) as APIReject)
})

export const updateAgreementRequirementSettings = createAsyncThunk<
  ClosingAgreementRequirementsModel[],
  ClosingAgreementRequirementsRequestModel,
  { rejectValue: APIReject }
>('closingRequirements/agreements', async (reqs, thunkApi) => {
  const response = await put(`${baseUrl}/agreement`, reqs)

  if (response.ok) return await response.json()
  return thunkApi.rejectWithValue((await response.json()) as APIReject)
})

export const updatePaymentRequirementSettings = createAsyncThunk<
  ClosingPaymentRequirementsModel[],
  ClosingPaymentRequirementsModel[],
  { rejectValue: APIReject }
>('closingRequirements/agreements', async (reqs, thunkApi) => {
  const response = await put(`${baseUrl}/payment`, reqs)

  if (response.ok) return await response.json()
  return thunkApi.rejectWithValue((await response.json()) as APIReject)
})

export const updateScheduleRequirementSettings = createAsyncThunk<
  ScheduleSettingsModel,
  ScheduleSettingsModel,
  { rejectValue: APIReject }
>('closingRequirements/agreements', async (reqs, thunkApi) => {
  const response = await put(`${baseUrl}/scheduling`, reqs)

  if (response.ok) return await response.json()
  return thunkApi.rejectWithValue((await response.json()) as APIReject)
})

const leadUrl = `${ServiceConfig.baseAPIUrl}${ServiceConfig.lead.odata}`
const oppUrl = `${ServiceConfig.baseAPIUrl}${ServiceConfig.opportunity.odata}`

type PartialEntity = Pick<
  ILead | IOpportunity,
  'closingRequirements' | 'services' | 'servicesCount'
>

export const updateEntityClosingRequirements = createAsyncThunk<
  PartialEntity,
  { entityId: number; entityType: ENTITY_TYPES }
>('entity/updateClosingRequirements', async (input, thunkApi) => {
  const { entityId: id, entityType } = input

  const qstring = buildQuery<PartialEntity>({
    filter: { id },
    expand: ['services'],
    select: ['closingRequirements', 'services', 'servicesCount'],
  })
  const url = entityType === ENTITY_TYPES.Lead ? leadUrl : oppUrl

  const response = await get(`${url}${qstring}`)

  if (response.ok) {
    const entities: ODataResponse<PartialEntity> = await response.json()

    if (entities.value.length > 0) {
      return entities.value[0]
    }
  }

  return thunkApi.rejectWithValue({})
})
